import axios from "axios";
//eslint-disable-next-line
import router from "@/router";
import store from "@/store/store"; // Import Vuex store
const BaseURL = process.env.VUE_APP_BASE_URL;

// Create API instances
const guestAPI = axios.create({ baseURL: BaseURL });
const getAPI = axios.create({ 
  baseURL: BaseURL, 
  withCredentials: true,
});

// Set default CSRF token header
getAPI.defaults.headers.common["X-CSRFToken"] = getCookie("csrftoken");

// Function to refresh token
let isRefreshing = false;
let refreshSubscribers = [];

async function refreshToken() {
  if (!localStorage.getItem("refresh_token")) {
    console.log("no refresh token")
    return Promise.reject("No refresh token available.");
  }

  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(`${BaseURL}accounts/login/refresh/`, {
      refresh: localStorage.getItem("refresh_token"),
    });
    console.log(response)
    if (response.status === 200) {
      const newAccessToken = response.data.access;
      localStorage.setItem("token", newAccessToken);

      // Retry all pending requests
      refreshSubscribers.forEach((callback) => callback(newAccessToken));
      refreshSubscribers = [];
      
      return newAccessToken;
    }
  } catch (error) {
    promptTokenExpire();
    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
}

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(name + "=")) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Handle expired token
function promptTokenExpire() {
  localStorage.clear();
  localStorage.setItem("currentPath", window.location.pathname);
  //router.push("/login");
  window.location.href = "/login";
}

function checkIfIsAdmin() {
  const params = new URLSearchParams(window.location.search);
  
  // Check for SSO callback parameters first
  const ssoSuccess = params.get('success');
  const accessToken = params.get('access_token') || params.get('access');
  const refreshToken = params.get('refresh_token') || params.get('refresh');
  const userParam = params.get('user');
  const email = params.get('email');
  const name = params.get('name');
  const created = params.get('created');
  
  // Check for regular authentication parameters
  const user = params.get('user');
  const firstname = params.get('firstname');
  const lastname = params.get('lastname');
  const organisation = params.get('organisation');
  const typeAccount = params.get('type_account');
  const role = params.get('role');
  
  // Handle SSO callback parameters
  // DO NOT save tokens if on /sso/account path - tokens will be saved after successful form submission
  if (ssoSuccess === 'true' && accessToken && refreshToken) {
    // Skip token saving for /sso/account path - first-time users need to complete form first
    if (window.location.pathname === '/sso/account') {
      console.log('SSO callback on /sso/account - tokens will be saved after successful form submission');
      return; // Don't save tokens yet, let the form handle it
    }
    
    console.log('Setting up SSO authentication from URL parameters...');
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    
    if (userParam) {
      try {
        const decodedUser = decodeURIComponent(userParam);
        let userData;
        try {
          userData = JSON.parse(decodedUser.replace(/'/g, '"'));
        } catch (jsonError) {
          userData = {};
          const idMatch = decodedUser.match(/UUID\(['"]([^'"]+)['"]\)/);
          if (idMatch) userData.id = idMatch[1];
          const firstnameMatch = decodedUser.match(/['"]firstname['"]:\s*['"]([^'"]+)['"]/);
          if (firstnameMatch) userData.firstname = firstnameMatch[1];
          const lastnameMatch = decodedUser.match(/['"]lastname['"]:\s*['"]([^'"]+)['"]/);
          if (lastnameMatch) userData.lastname = lastnameMatch[1];
          const typeAccountMatch = decodedUser.match(/['"]type_account['"]:\s*['"]([^'"]+)['"]/);
          if (typeAccountMatch) userData.type_account = typeAccountMatch[1];
          const roleMatch = decodedUser.match(/['"]role['"]:\s*['"]([^'"]+)['"]/);
          if (roleMatch) userData.role = roleMatch[1];
          const serviceMatch = decodedUser.match(/['"]service['"]:\s*['"]([^'"]+)['"]/);
          if (serviceMatch) userData.service = serviceMatch[1];
          const orgMatch = decodedUser.match(/['"]organisation['"]:\s*UUID\(['"]([^'"]+)['"]\)/);
          if (orgMatch) userData.organisation = orgMatch[1];
          const codeOrgMatch = decodedUser.match(/['"]code_organisation['"]:\s*['"]([^'"]+)['"]/);
          if (codeOrgMatch) userData.code_organisation = codeOrgMatch[1];
          const isCompletedMatch = decodedUser.match(/['"]is_completed['"]:\s*(True|False)/);
          if (isCompletedMatch) userData.is_completed = isCompletedMatch[1] === 'True';
        }
        
        // Determine if user is new based on created parameter or is_completed flag
        const isNewUser = created === 'true' || userData.is_completed === false || userData.is_completed === 'False';
        
        if (userData.id) localStorage.setItem('user', userData.id);
        if (userData.firstname) localStorage.setItem('firstname', userData.firstname);
        if (userData.lastname) localStorage.setItem('lastname', userData.lastname);
        if (userData.type_account) localStorage.setItem('type_account', userData.type_account);
        if (userData.role) localStorage.setItem('role', userData.role);
        if (userData.organisation) localStorage.setItem('organisation', userData.organisation);
        if (userData.code_organisation) localStorage.setItem('code_organisation', userData.code_organisation);
        if (userData.service) localStorage.setItem('service', userData.service);
        if (userData.is_completed !== undefined) localStorage.setItem('is_completed', userData.is_completed);
        
        // Redirect based on whether user is new or existing
        if (isNewUser) {
          // New user - redirect to activation page
          console.log('New SSO user detected, redirecting to activation page...');
          if (email) localStorage.setItem('sso_email', email);
          if (name) localStorage.setItem('sso_name', name);
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          setTimeout(() => {
            window.location.href = '/sso/account';
          }, 100);
          return;
        } else {
          // Existing user - redirect to dashboard
          console.log('Existing SSO user detected, redirecting to dashboard...');
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 100);
          return;
        }
      } catch (error) {
        if (email) localStorage.setItem('email', decodeURIComponent(email));
        if (name) localStorage.setItem('firstname', decodeURIComponent(name));
        // If we can't parse user data, check created parameter
        const isNewUser = created === 'true';
        if (isNewUser) {
          if (email) localStorage.setItem('sso_email', email);
          if (name) localStorage.setItem('sso_name', name);
          setTimeout(() => {
            window.location.href = '/sso/account';
          }, 100);
        } else {
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 100);
        }
        return;
      }
    } else if (email) {
      localStorage.setItem('email', decodeURIComponent(email));
      if (name) localStorage.setItem('firstname', decodeURIComponent(name));
      // If no user param, check created parameter
      const isNewUser = created === 'true';
      if (isNewUser) {
        localStorage.setItem('sso_email', email);
        if (name) localStorage.setItem('sso_name', name);
        setTimeout(() => {
          window.location.href = '/sso/account';
        }, 100);
      } else {
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 100);
      }
      return;
    }
    
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    return;
  }
  
  // Handle regular authentication parameters
  if (accessToken && refreshToken && user && firstname && lastname && organisation && typeAccount && role) {
    console.log('Setting up regular authentication from URL parameters...');
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', user);
    localStorage.setItem('firstname', firstname);
    localStorage.setItem('lastname', lastname);
    localStorage.setItem('organisation', organisation);
    localStorage.setItem('type_account', typeAccount);
    localStorage.setItem('role', role);
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    console.log('Regular authentication setup complete from URL parameters');
  }
}

// Attach token to requests
getAPI.interceptors.request.use(
  (config) => {
    checkIfIsAdmin()
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token expiration handling
getAPI.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    console.log("error here intercept", error)
    if (!error.response) return Promise.reject(error);
    
    const originalRequest = error.config;

    if (
      error.response.status === 401 &&
      (error.response.data.code === "token_not_valid" ||
        error.response.data.detail === "Given token not valid for any token type")
    ) {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest); // Retry the failed request
        }
      } catch {
        store.dispatch("loader/triggerLoading", false);
        promptTokenExpire();
      }
    }
    // if (error.response.status == 403) {
    //   //console.log("response from axios apiiiiiiii")
    //   //Upgrade your plan to perform this action.
    //   if (error.response.data?.message?.startsWith("You do not have permission") || error.response.data?.detail?.startsWith("You do not have permission")) {
    //     store.dispatch("error/triggerError", "Vous n'avez pas la permission d'effectuer cette action");
    //   } else if (error.response.data?.detail?.startsWith("Upgrade your plan")) {
    //     store.dispatch("error/triggerError", "Changer votre formule pour beneficier de cette fonctionnalite");
    //   }
    // }
    return Promise.reject(error);
  }
);

export { getAPI, guestAPI, BaseURL };
